/**
 * @module services/isbnSearchService
 * @description Orchestrates ISBN search across multiple catalog services with priority hierarchy
 *
 * Search priority:
 * 1. Sudoc (French academic catalogs) - highest priority
 * 2. BnF, Swisscovery, Harvard Library - searched if Sudoc fails
 * 3. Open Library - last resort
 *
 * Result selection criteria:
 * - Prefer records with: title, author, year, place, publisher
 * - If multiple matches, prefer most recent edition
 */

import {
  sudocService,
  bnfService,
  swisscoveryService,
  harvardLibraryService,
  openLibraryService,
} from '@services/catalogs';
import type { ItemData } from '@types';

// =============================================================================
// TYPES
// =============================================================================

export interface ISBNSearchResult {
  item: Partial<ItemData>;
  source: 'sudoc' | 'bnf' | 'swisscovery' | 'harvardLibrary' | 'openLibrary';
  score: number;
  identifier: string;
}

export interface ISBNSearchResponse {
  success: boolean;
  result?: ISBNSearchResult;
  searchedSources: string[];
  error?: string;
}

// =============================================================================
// SCORING
// =============================================================================

/**
 * Calculate a quality score for a catalog record
 * Higher score = better quality record
 */
function calculateScore(item: Partial<ItemData>): number {
  let score = 0;

  // Essential fields (10 points each)
  if (item.title && item.title.trim()) score += 10;
  if (item.creators && item.creators.length > 0) score += 10;
  if (item.date) score += 10;
  if (item.place) score += 10;
  if (item.publisher) score += 10;

  // Bonus fields (5 points each)
  if (item.ISBN) score += 5;
  if (item.numPages) score += 5;
  if (item.language) score += 5;
  if (item.edition) score += 5;

  // Extra bonus for recent editions (based on year)
  if (item.date) {
    const year = parseInt(item.date, 10);
    if (!isNaN(year) && year >= 2000) {
      score += Math.min(10, year - 2000); // Up to 10 bonus points for recent years
    }
  }

  return score;
}

/**
 * Select the best result from a list of candidates
 */
function selectBestResult(results: ISBNSearchResult[]): ISBNSearchResult | undefined {
  if (results.length === 0) return undefined;
  if (results.length === 1) return results[0];

  // Sort by score descending
  return results.sort((a, b) => b.score - a.score)[0];
}

// =============================================================================
// CATALOG SEARCH FUNCTIONS
// =============================================================================

async function searchSudoc(isbn: string): Promise<ISBNSearchResult[]> {
  try {
    const result = await sudocService.search(isbn, {
      index: 'isb',
      maximumRecords: 5,
    });

    return result.records.map((record) => ({
      item: record.item,
      source: 'sudoc' as const,
      score: calculateScore(record.item),
      identifier: record.ppn,
    }));
  } catch (error) {
    console.error('Sudoc search failed:', error);
    return [];
  }
}

async function searchBnf(isbn: string): Promise<ISBNSearchResult[]> {
  try {
    const result = await bnfService.search(isbn, {
      index: 'bib.isbn',
      maximumRecords: 5,
    });

    return result.records.map((record) => ({
      item: record.item,
      source: 'bnf' as const,
      score: calculateScore(record.item),
      identifier: record.ark,
    }));
  } catch (error) {
    console.error('BnF search failed:', error);
    return [];
  }
}

async function searchSwisscovery(isbn: string): Promise<ISBNSearchResult[]> {
  try {
    const result = await swisscoveryService.search(isbn, {
      index: 'alma.isbn',
      maximumRecords: 5,
    });

    return result.records.map((record) => ({
      item: record.item,
      source: 'swisscovery' as const,
      score: calculateScore(record.item),
      identifier: record.mmsId,
    }));
  } catch (error) {
    console.error('Swisscovery search failed:', error);
    return [];
  }
}

async function searchHarvardLibrary(isbn: string): Promise<ISBNSearchResult[]> {
  try {
    const result = await harvardLibraryService.search(isbn, {
      searchType: 'isbn',
      maxResults: 5,
    });

    return result.records.map((record) => ({
      item: record.item,
      source: 'harvardLibrary' as const,
      score: calculateScore(record.item),
      identifier: record.id,
    }));
  } catch (error) {
    console.error('Harvard Library search failed:', error);
    return [];
  }
}

async function searchOpenLibrary(isbn: string): Promise<ISBNSearchResult[]> {
  try {
    const result = await openLibraryService.search(isbn, {
      index: 'isbn',
      limit: 5,
    });

    return result.records.map((record) => ({
      item: record.item,
      source: 'openLibrary' as const,
      score: calculateScore(record.item),
      identifier: record.olid,
    }));
  } catch (error) {
    console.error('Open Library search failed:', error);
    return [];
  }
}

// =============================================================================
// MAIN SEARCH FUNCTION
// =============================================================================

/**
 * Search for an ISBN across multiple catalog services
 *
 * Priority:
 * 1. Sudoc (if found, return immediately)
 * 2. BnF, Swisscovery, Harvard Library (search in parallel)
 * 3. Open Library (last resort)
 */
export async function searchISBN(isbn: string): Promise<ISBNSearchResponse> {
  const cleanISBN = isbn.replace(/[-\s]/g, '');
  const searchedSources: string[] = [];

  console.log(`Searching ISBN: ${cleanISBN}`);

  // Step 1: Search Sudoc first (highest priority)
  searchedSources.push('sudoc');
  const sudocResults = await searchSudoc(cleanISBN);

  if (sudocResults.length > 0) {
    const bestResult = selectBestResult(sudocResults);
    if (bestResult && bestResult.score >= 30) {
      // At least title + author + date
      console.log('Found in Sudoc with good score:', bestResult.score);
      return {
        success: true,
        result: bestResult,
        searchedSources,
      };
    }
  }

  // Step 2: Search BnF, Swisscovery, Harvard Library in parallel
  searchedSources.push('bnf', 'swisscovery', 'harvardLibrary');

  const [bnfResults, swisscoveryResults, harvardResults] = await Promise.all([
    searchBnf(cleanISBN),
    searchSwisscovery(cleanISBN),
    searchHarvardLibrary(cleanISBN),
  ]);

  // Combine results from tier 2 sources
  const tier2Results = [...bnfResults, ...swisscoveryResults, ...harvardResults];

  // If we had a Sudoc result (even with low score), prefer it over tier 2
  if (sudocResults.length > 0) {
    const allResults = [...sudocResults, ...tier2Results];
    const bestResult = selectBestResult(allResults);

    // Boost Sudoc results by 20 points for priority
    const sudocBest = selectBestResult(sudocResults);
    if (sudocBest && bestResult) {
      if (sudocBest.score + 20 >= bestResult.score) {
        console.log('Preferring Sudoc result with priority boost');
        return {
          success: true,
          result: sudocBest,
          searchedSources,
        };
      }
    }

    if (bestResult) {
      console.log(`Found in ${bestResult.source} with score:`, bestResult.score);
      return {
        success: true,
        result: bestResult,
        searchedSources,
      };
    }
  }

  if (tier2Results.length > 0) {
    const bestResult = selectBestResult(tier2Results);
    if (bestResult) {
      console.log(`Found in ${bestResult.source} with score:`, bestResult.score);
      return {
        success: true,
        result: bestResult,
        searchedSources,
      };
    }
  }

  // Step 3: Last resort - Open Library
  searchedSources.push('openLibrary');
  const openLibraryResults = await searchOpenLibrary(cleanISBN);

  if (openLibraryResults.length > 0) {
    const bestResult = selectBestResult(openLibraryResults);
    if (bestResult) {
      console.log('Found in Open Library with score:', bestResult.score);
      return {
        success: true,
        result: bestResult,
        searchedSources,
      };
    }
  }

  // No results found
  console.log('ISBN not found in any catalog');
  return {
    success: false,
    searchedSources,
    error: 'ISBN not found in any catalog',
  };
}

export default {
  searchISBN,
};
